/*============================================
   HoneyBall Stadium Experience
   assets/honeyball-stadium.js
   Scroll-driven cinematic animation engine
   ============================================ */

(function () {
  'use strict';

  const INTENSITY_MAP = { low: 0.5, medium: 1.0, high: 1.5 };

  function lerp(a, b, t) {
    return a + (b - a) * Math.max(0, Math.min(1, t));
  }

  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  function buildSeatInfoHTML(data) {
    const linkHTML = data.url
      ? `<a class="hbs-seat-link" href="${data.url}">Shop This Section</a>`
      : '';
    return `
      <p class="hbs-seat-number">Section ${data.section}</p>
      <p class="hbs-seat-level">${data.level}</p>
      <p class="hbs-seat-desc">${data.description}</p>
      <p class="hbs-seat-price">${data.price}</p>
      <p class="hbs-seat-avail">${data.availability}</p>
      ${linkHTML}
    `;
  }

  function initSeatMap(section) {
    const svg = section.querySelector('.hbs-seat-svg');
    if (!svg) return;
    const infoPanel = section.querySelector('.hbs-seat-info');
    const infoContent = section.querySelector('.hbs-seat-info-content');
    const closeBtn = section.querySelector('.hbs-seat-info-close');
    if (!infoPanel || !infoContent) return;

    const paths = svg.querySelectorAll('.hbs-seat-path');

    paths.forEach(function (path) {
      const color = path.dataset.color;
      if (color) path.setAttribute('fill', color);
    });

    function showInfo(path) {
      const data = {
        section: path.dataset.section || '',
        level: path.dataset.level || '',
        description: path.dataset.description || '',
        price: path.dataset.price || '',
        availability: path.dataset.availability || '',
        url: path.dataset.url || ''
      };
      infoContent.innerHTML = buildSeatInfoHTML(data);
      infoPanel.classList.add('is-visible');
    }

    paths.forEach(function (path) {
      path.addEventListener('click', function () { showInfo(path); });
      path.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showInfo(path); }
      });
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        infoPanel.classList.remove('is-visible');
      });
    }
  }

  function initSection(section) {
    const enableDesktop = section.dataset.enableDesktop !== 'false';
    const enableMobile = section.dataset.enableMobile !== 'false';
    const isMobile = window.innerWidth <= 768;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const respectReduced = section.dataset.reducedMotion !== 'false';

    if (isMobile && !enableMobile) return;
    if (!isMobile && !enableDesktop) return;
    if (reducedMotion && respectReduced) return;

    const intensity = INTENSITY_MAP[section.dataset.intensity] || 1.0;
    const outer = section.querySelector('.hbs-outer');
    const sticky = section.querySelector('.hbs-sticky-container');
    const progressBar = section.querySelector('.hbs-progress-bar');
    const scenes = Array.from(section.querySelectorAll('.hbs-scene'));

    if (!outer || !sticky || scenes.length === 0) return;

    initSeatMap(section);

    let currentScene = 0;
    let rafId = null;
    let lastProgress = -1;

    function getProgress() {
      const rect = outer.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return 0;
      return clamp(-rect.top / scrollable, 0, 1);
    }

    function activateScene(index) {
      scenes.forEach(function (s, i) {
        s.classList.toggle('is-active', i === index);
        s.classList.toggle('is-prev', i < index);
      });
      currentScene = index;
    }

    function applyZoom(scene, progress, sceneStart, sceneEnd) {
      const img = scene.querySelector('img');
      if (!img) return;
      const zoomStart = parseFloat(scene.dataset.zoomStart) || 1.0;
      const zoomEnd = parseFloat(scene.dataset.zoomEnd) || 1.1;
      const localProgress = sceneEnd > sceneStart
        ? clamp((progress - sceneStart) / (sceneEnd - sceneStart), 0, 1)
        : 0;
      const scale = lerp(zoomStart, zoomEnd, localProgress * intensity);
      img.style.transform = `scale(${scale})`;
    }

    function onScroll() {
      const progress = getProgress();
      if (Math.abs(progress - lastProgress) < 0.0005) return;
      lastProgress = progress;

      if (progressBar) progressBar.style.width = (progress * 100) + '%';

      const sceneCount = scenes.length;
      const sceneSize = 1 / sceneCount;
      const activeIndex = clamp(Math.floor(progress / sceneSize), 0, sceneCount - 1);

      if (activeIndex !== currentScene) activateScene(activeIndex);

      scenes.forEach(function (scene, i) {
        const sceneStart = i * sceneSize;
        const sceneEnd = sceneStart + sceneSize;
        applyZoom(scene, progress, sceneStart, sceneEnd);
      });
    }

    function tick() {
      onScroll();
      rafId = requestAnimationFrame(tick);
    }

    function startAnimation() {
      if (!rafId) rafId = requestAnimationFrame(tick);
    }

    function stopAnimation() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { startAnimation(); }
        else { stopAnimation(); }
      });
    }, { threshold: 0 });

    observer.observe(outer);
    activateScene(0);
  }

  function init() {
    const sections = document.querySelectorAll('.honeyball-stadium-section');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

